import {
    businessIndustryLabels,
    BusinessProfileDetails,
    BusinessProfileReviewSection,
    businessProfileReviewSectionLabels,
    BusinessRepresentative,
    businessTypeLabels,
    formatEin,
    optInMethodLabels,
    privacyPolicyStatementLabels,
    representativePositionLabels,
} from "@sp-crm/core";
import { ReadOnlyField } from "components/ui/read-only-field";
import React from "react";

const testIdFor = (label: string): string =>
    `business-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

const Field: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
    <ReadOnlyField label={label} testId={testIdFor(label)}>
        {value || null}
    </ReadOnlyField>
);

const Section: React.FC<{
    section: BusinessProfileReviewSection;
    children: React.ReactNode;
}> = ({ section, children }) => (
    <section className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6 space-y-4">
        <h3 className="text-base font-medium text-gray-900">
            {businessProfileReviewSectionLabels[section]}
        </h3>
        {children}
    </section>
);

const Representative: React.FC<{
    heading: string;
    representative: BusinessRepresentative;
}> = ({ heading, representative }) => (
    <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">{heading}</h4>
        <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" value={representative.firstName} />
            <Field label="Last name" value={representative.lastName} />
            <Field label="Job title" value={representative.title} />
            <Field
                label="Position"
                value={
                    representative.position
                        ? representativePositionLabels[representative.position]
                        : null
                }
            />
            <Field label="Work email" value={representative.email} />
            <Field label="Phone" value={representative.phone} />
        </div>
    </div>
);

const BusinessSection: React.FC<{ details: BusinessProfileDetails }> = ({ details }) => (
    <Section section="business">
        <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Legal business name" value={details.legalName} />
            <Field label="Name customers know you by" value={details.doingBusinessAs} />
            <Field
                label="Business type"
                value={
                    details.businessType ? businessTypeLabels[details.businessType] : null
                }
            />
            <Field
                label="Industry"
                value={details.industry ? businessIndustryLabels[details.industry] : null}
            />
            <Field label="EIN" value={formatEin(details.ein)} />
            <Field label="Website" value={details.website} />
            <Field label="Facebook or LinkedIn page" value={details.socialPage} />
        </div>
    </Section>
);

const AddressSection: React.FC<{ details: BusinessProfileDetails }> = ({ details }) => (
    <Section section="address">
        <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Street address" value={details.address.street} />
            <Field label="Suite or unit" value={details.address.suite} />
            <Field label="City" value={details.address.city} />
            <Field label="State" value={details.address.state} />
            <Field label="ZIP" value={details.address.zip} />
        </div>
    </Section>
);

const RepresentativeSection: React.FC<{ details: BusinessProfileDetails }> = ({
    details,
}) => (
    <Section section="representative">
        <Representative
            heading="Representative"
            representative={details.representative}
        />
        {details.secondRepresentative ? (
            <Representative
                heading="Second representative"
                representative={details.secondRepresentative}
            />
        ) : null}
    </Section>
);

const TextingSection: React.FC<{ details: BusinessProfileDetails }> = ({ details }) => (
    <Section section="texting">
        <Field
            label="How people give their number"
            value={details.optInMethods
                .map(method => optInMethodLabels[method])
                .join(", ")}
        />
        {details.sampleMessages.map((message, index) => (
            <Field key={index} label={`Sample message ${index + 1}`} value={message} />
        ))}
        <Field label="Privacy policy link" value={details.privacyPolicyUrl} />
        <Field
            label="Privacy policy checks"
            value={details.privacyPolicyStatements
                .map(statement => privacyPolicyStatementLabels[statement])
                .join(", ")}
        />
    </Section>
);

export const BusinessDetails: React.FC<{ details: BusinessProfileDetails }> = ({
    details,
}) => (
    <div className="space-y-4">
        <BusinessSection details={details} />
        <AddressSection details={details} />
        <RepresentativeSection details={details} />
        <TextingSection details={details} />
    </div>
);
